/**
 * Component CSS styles for the survey widget.
 * Includes avatar, question title/description, buttons, branding, error,
 * and layout utility classes. Uses BEM naming with ph- prefix.
 */
export const componentsCSS = `
    /* ===== Header ===== */
    .ph-header {
        display: flex;
        min-height: 40px;
        padding: 12px 16px;
        justify-content: space-between;
        align-items: center;
        align-self: stretch;
        box-sizing: border-box;
        margin: -16px -16px 12px -16px;
        border-radius: 8px 8px 0 0;
    }

    .ph-header__close {
        cursor: pointer;
        display: flex;
        align-items: center;
    }

    .ph-header__branding {
        font-size: 14px;
        display: flex;
        align-items: center;
    }

    /* ===== Avatar ===== */
    .ph-avatar-container {
        display: flex;
        align-items: center;
        text-align: left;
    }

    .ph-avatar {
        width: 40px;
        height: 40px;
        border-radius: 50%;
        display: inline-block;
        vertical-align: middle;
        margin-right: 8px;
        overflow: hidden;
        object-fit: cover;
        flex-shrink: 0;
    }

    .ph-avatar img,
    .ph-avatar image,
    .ph-avatar svg {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
        margin-right: 0;
        border-radius: 50%;
    }

    .ph-avatar__info {
        display: inline-block;
        vertical-align: middle;
        min-width: 0;
        flex: 1 1 auto;
    }

    .ph-avatar__name {
        font-size: var(--ph-font-normal);
        color: var(--ph-text-color);
        font-weight: 600;
        line-height: normal;
        word-break: break-word;
        overflow-wrap: break-word;
    }

    .ph-avatar__title {
        font-size: var(--ph-font-normal);
        color: var(--ph-text-color-50);
        font-weight: 400;
        line-height: normal;
        word-break: break-word;
        overflow-wrap: break-word;
    }

    /* ===== Question image (Thank You Message) ===== */
    .ph-question__image-container {
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        margin: 0;
        margin-bottom: 4px;
        padding: 0;
        overflow: hidden;
        border-radius: 8px;
    }

    .ph-question__image {
        width: 100%;
        max-height: 200px;
        object-fit: cover;
        border-radius: 8px;
        display: block;
        padding: 0;
        margin: 0;
    }

    /* ===== Question title ===== */
    .ph-question__title {
        color: var(--ph-text-color);
        font-size: var(--ph-font-large);
        line-height: normal;
        font-weight: 600;
        white-space: pre-line;
    }

    /* ===== Question description ===== */
    .ph-question__description {
        word-wrap: break-word;
        color: var(--ph-text-color-75);
        font-size: var(--ph-font-large);
        line-height: normal;
        font-weight: 400;
        white-space: pre-line;
        text-align: left;
    }

    /* ===== Primary button (Next/Submit) ===== */
    .ph-btn--primary {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16.5px;
        text-align: center;
        background-color: var(--ph-element-color);
        color: var(--ph-btn-text-color);
        border: none;
        border-radius: 4px;
        cursor: pointer;
        line-height: normal;
        font-weight: 600;
        font-family: var(--ph-font-family);
        padding: 12px 16px;
        height: 45px;
        box-sizing: border-box;
        margin-top: 16px;
        white-space: nowrap;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
    }

    .ph-btn--primary:hover {
        background-color: var(--ph-element-color);
        opacity: 0.6;
    }

    .ph-btn--primary:focus-visible {
        outline: 2px solid var(--ph-text-color);
        outline-offset: 2px;
    }

    /* ===== Secondary button (Skip) ===== */
    .ph-btn--secondary {
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: var(--ph-font-large);
        text-align: center;
        background-color: transparent;
        color: var(--ph-element-color);
        border: solid 2px var(--ph-element-color);
        border-radius: 4px;
        cursor: pointer;
        line-height: normal;
        font-weight: 600;
        font-family: var(--ph-font-family);
        padding: 12px 16px;
        height: 45px;
        box-sizing: border-box;
        margin-top: 16px;
        white-space: nowrap;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        width: 100%;
    }

    .ph-btn--secondary:focus-visible {
        outline: 2px solid var(--ph-element-color);
        outline-offset: 2px;
    }

    /* ===== Generic action button ===== */
    .ph-btn--action {
        font-size: var(--ph-font-large);
        text-align: center;
        height: 40px;
        width: 200px;
        background-color: var(--ph-element-color);
        color: var(--ph-btn-text-color);
        border: none;
        border-radius: 8px;
        cursor: pointer;
        line-height: 1.2;
        font-weight: 600;
        margin-top: 8px;
        font-family: var(--ph-font-family);
    }

    /* ===== Branding ===== */
    .ph-branding {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        gap: 4px;
        margin-top: 8px;
    }

    .ph-branding:empty {
        display: none;
    }

    /* Custom branding shown centered at the top when the header is disabled */
    .ph-branding--top {
        margin-top: 0;
        margin-bottom: 16px;
    }

    .ph-branding__text {
        font-size: var(--ph-font-small);
        color: var(--ph-text-color-75);
        font-weight: 400;
        display: flex;
        align-items: center;
    }

    .ph-branding__logo img {
        height: 20px;
        width: auto;
        display: block;
    }

    .ph-branding__logo svg {
        height: 12px;
        width: auto;
        display: block;
    }

    .ph-branding__logo a {
        text-decoration: none;
    }

    .ph-branding a:focus-visible {
        outline: 2px solid var(--ph-element-color);
        outline-offset: 2px;
    }

    /* ===== Error message ===== */
    .ph-error {
        color: #DA1515;
        font-size: var(--ph-font-small);
        font-weight: 400;
        margin-top: 8px;
        padding: 5px;
        line-height: 14px;
        border: none;
        text-align: center;
        background-color: #FFD2D2;
        border-radius: 4px;
    }

    .ph-warning {
        color: #CEAB00;
        font-size: var(--ph-font-small);
        font-weight: 400;
        margin-top: 16px;
        padding: 5px;
        line-height: 14px;
        border: none;
        text-align: center;
        background-color: rgba(206, 171, 0, 0.15);
        border-radius: 4px;
    }

    /* ===== Layout utilities ===== */
    .ph-center {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        gap: 20px;
    }

    .ph-space-between {
        display: flex;
        justify-content: space-between;
        align-items: center;
        width: 100%;
    }

    /* ===== Minimized Survey Button ===== */
    .ph-survey-minimized {
        all: unset;
        position: fixed;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 8px 16px;
        box-sizing: border-box;
        border-radius: 8px;
        border-bottom-right-radius: 0px;
        cursor: pointer;
        transition: opacity 0.2s ease;
        background-color: var(--ph-bg-color);
        transform: translate(var(--ph-minimized-tx, 0), var(--ph-minimized-ty, 0));
    }

    .ph-survey-minimized:hover {
        opacity: 0.9;
    }

    .ph-survey-minimized:focus-visible {
        outline: 2px solid var(--ph-text-color);
        outline-offset: 2px;
    }

    /* Anchors inside the preview pane instead of the viewport */
    .ph-survey-minimized--preview {
        position: absolute;
    }

    /* --- Speech bubble variant --- */
    .ph-survey-minimized--bubble {
        height: 108px;
        min-width: 76px;
    }

    .ph-survey-minimized--bubble.ph-minimized-h--left { left: 32px; }
    .ph-survey-minimized--bubble.ph-minimized-h--middle { left: 50%; --ph-minimized-tx: -50%; }
    .ph-survey-minimized--bubble.ph-minimized-h--right { right: 32px; }
    .ph-survey-minimized--bubble.ph-minimized-v--top { top: 32px; }
    .ph-survey-minimized--bubble.ph-minimized-v--middle { top: 50%; --ph-minimized-ty: -50%; }
    .ph-survey-minimized--bubble.ph-minimized-v--bottom { bottom: 32px; }

    /* Bubble anchored left: tail and flattened corner mirror to the left */
    .ph-survey-minimized--tail-left {
        border-bottom-right-radius: 8px;
        border-bottom-left-radius: 0px;
    }

    .ph-minimized-tail {
        position: absolute;
        bottom: -6px;
        right: 0;
    }

    .ph-minimized-tail--left {
        right: auto;
        left: 0;
    }

    .ph-minimized-icon {
        height: 54px;
        width: auto;
    }

    div.ph-minimized-icon {
        display: flex;
        align-items: center;
        justify-content: center;
    }

    img.ph-minimized-icon {
        object-fit: contain;
    }

    .ph-minimized-text {
        font-size: 14px;
        font-family: var(--ph-font-family);
        color: var(--ph-text-color);
        font-weight: bold;
    }

    .ph-minimized-text--truncate {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 300px;
    }

    .ph-minimized-text--vertical {
        writing-mode: vertical-rl;
        transform: rotate(180deg);
    }

    /* --- Feedback badge variant (sits flush against the screen edge) --- */
    .ph-feedback-badge {
        padding: 12px 6px;
        gap: 8px;
        border: none;
        border-radius: 4px;
    }

    .ph-feedback-badge.ph-minimized-h--left { left: 0; border-radius: 0 4px 4px 0; }
    .ph-feedback-badge.ph-minimized-h--right { right: 0; border-radius: 4px 0 0 4px; }
    .ph-feedback-badge.ph-minimized-h--middle {
        flex-direction: row;
        bottom: 0;
        left: 50%;
        --ph-minimized-tx: -50%;
        border-radius: 4px 4px 0 0;
    }
    .ph-feedback-badge.ph-minimized-v--top { top: 3vh; }
    .ph-feedback-badge.ph-minimized-v--middle { top: 50%; --ph-minimized-ty: -50%; }
    .ph-feedback-badge.ph-minimized-v--bottom { bottom: 3vh; }

    /* On small screens the bubble docks to the bottom corner; the badge keeps
       its configured, edge-flush position. The preview reflects the configured
       position regardless of the editor's window size. */
    @media (max-width: 768px) {
        .ph-survey-minimized--bubble {
            height: 80px;
            min-width: 56px;
        }

        .ph-minimized-icon {
            height: 40px;
        }
    }
`;
